import React from "react";

import {
  Container,
  Form,
  Small,
  Action,
  Wrapper,
  Picture,
  Timestamp,
  Profile,
  Name,
  Job,
  ProfileDetails,
  PictureOverlay,
  Tag,
  TagTitle,
} from "./styles/settings";

import { Camera01Icon, UserIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { Popconfirm, PopconfirmProps, Avatar, Dropdown } from "antd";
import moment from "moment";

import { ReactComponent as PrimeIcon } from "src/assets/prime.svg";
import { ReactComponent as EliteIcon } from "src/assets/elite.svg";

const Settings = function ContainerSettings({ children, ...rest }) {
  return <Container {...rest}>{children}</Container>;
};

Settings.Form = function SettingForm({ children, ...rest }) {
  return <Form {...rest}>{children}</Form>;
};

Settings.Small = function SettingSmallText({ children, ...rest }) {
  return <Small {...rest}>{children}</Small>;
};

Settings.Action = function SettingAction({
  children,
  ...rest
}: {
  children?: React.ReactNode;
}) {
  return <Action {...rest}>{children}</Action>;
};

Settings.Popconfirm = function SettingConfirm({
  children,
  ...rest
}: PopconfirmProps) {
  return <Popconfirm {...rest}>{children}</Popconfirm>;
};

Settings.Wrapper = function SettingWrapper({ children, ...rest }) {
  return <Wrapper {...rest}>{children}</Wrapper>;
};

Settings.Picture = function SettingPicture({ src, menu }) {
  /* 48, on the way down from the 128 this once was: the avatar sits on one
     line with the name beside it and a control opposite, and at 64 it was
     taller than the two lines of text it belongs to. Football's is 46. */
  const avatar = (
    <Avatar
      /* A circle. A person's picture is a person, and every other avatar in
         this product — the sidebar's, the tables' — is round; the square one
         here read as an image placeholder rather than as someone. */
      shape="circle"
      size={48}
      src={src}
      icon={<HugeIcon icon={UserIcon} size={20} />}
    />
  );

  if (!menu) return <Picture>{avatar}</Picture>;

  return (
    /* The picture IS the button. The menu used to hang off a 24px chip on the
       circle's corner; now the whole portrait opens it, and what it does is
       written on the portrait rather than beside it. */
    <Dropdown overlay={menu} placement="bottomLeft" trigger={["click"]}>
      <Picture
        role="button"
        tabIndex={0}
        aria-label="Change your picture"
        title="Change your picture"
      >
        {avatar}
        <PictureOverlay aria-hidden="true">
          <HugeIcon icon={Camera01Icon} size={16} />
        </PictureOverlay>
      </Picture>
    </Dropdown>
  );
};

Settings.Profile = function SettingsProfile({ children }) {
  return <Profile>{children}</Profile>;
};

Settings.Name = function SettingsName({ children, ...rest }) {
  return <Name {...rest}>{children}</Name>;
};

Settings.Tag = function SettingsName({ children, type, ...rest }) {
  return (
    <Tag {...rest}>
      {/* 12, not 18: the glyph was wider than the word beside it inside a
          20px pill, so the mark read as the badge and the tier's name as its
          caption. */}
      {type === "elite" && <EliteIcon fill={"white"} width={12} height={12} />}
      {type === "prime" && <PrimeIcon fill={"white"} width={12} height={12} />}
      <TagTitle>{children}</TagTitle>
    </Tag>
  );
};

Settings.Job = function SettingsJob({ children, ...rest }) {
  return <Job {...rest}>{children}</Job>;
};

Settings.ProfileDetails = function SettingsProfileDetails({
  children,
  ...rest
}) {
  return <ProfileDetails {...rest}>{children}</ProfileDetails>;
};

const DATE_FORMAT = "D MMM YYYY";

Settings.Timestamp = function SettingTimeStamp({ created, updated }) {
  return (
    /* One line, in the product's own date format. It was two right-aligned
       lines of "account created : 2024-03-11 9:12AM" — a space before the
       colon, an ISO date and a 12-hour clock welded to its meridiem — given
       the top-right corner of the panel, which is the most valuable place on
       it and was spending it on an audit trail. */
    <Timestamp>
      <span>Created {moment(created).format(DATE_FORMAT)}</span>
      <span aria-hidden="true">·</span>
      <span>Updated {moment(updated).format(DATE_FORMAT)}</span>
    </Timestamp>
  );
};

export default Settings;
